import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { ArrowLeft, RotateCcw, Coins, Trophy } from "lucide-react";
import ArcadeNav from "../../arcade/ArcadeNav";
import { useAuth } from "../../contexts/AuthContext";
import { usePresence } from "../../game/presence";
import useSEO from "../../seo/useSEO";
import { SEO_GAMES } from "../../seo/seoData";
import GameSEOContent from "../../seo/GameSEOContent";
import ArcadeFooter from "../../arcade/ArcadeFooter";
import { newGame, tryDragMove, isGameOver, SIZE, WIN_VALUE } from "./MergeEngine";
import { tileMeta, tileImage } from "./tiles";
import "./merge.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BEST_KEY = "zombie-merge-best";

export default function MergeScreen() {
  useSEO(SEO_GAMES["zombie-merge"]);
  const { user, refresh } = useAuth();

  const [state, setState] = useState(() => newGame());
  const [over, setOver] = useState(false);
  const [bigWin, setBigWin] = useState(false);
  const [winSubmitted, setWinSubmitted] = useState(false);
  const [bestScore, setBestScore] = useState(() =>
    parseInt(localStorage.getItem(BEST_KEY) || "0", 10)
  );
  const [drag, setDrag] = useState(null); // { tileId, value, x, y, offsetX, offsetY, startX, startY, isDragging }
  const boardRef = useRef(null);

  usePresence(true, "zombie-merge");

  // Persist personal best
  useEffect(() => {
    if (state.score > bestScore) {
      setBestScore(state.score);
      localStorage.setItem(BEST_KEY, String(state.score));
    }
  }, [state.score, bestScore]);

  // Game-over detection
  useEffect(() => {
    if (!over && isGameOver(state)) {
      setOver(true);
      submitRun(state.score, state.moves);
    }
  }, [state, over]);

  async function submitRun(score, moves) {
    if (winSubmitted) return;
    setWinSubmitted(true);
    if (!user) return;
    try {
      const bestTile = Math.max(0, ...state.tiles.map((t) => t.value));
      const res = await axios.post(`${API}/games/merge/win`, {
        score,
        moves,
        best_tile: bestTile,
      });
      if (res.data?.coins_awarded > 0) {
        toast.success(`+${res.data.coins_awarded} coins!`, { duration: 4000 });
        await refresh();
      }
    } catch (_e) {
      /* silent */
    }
  }

  function handleNew() {
    setState(newGame());
    setOver(false);
    setBigWin(false);
    setWinSubmitted(false);
  }

  // ---- Drag handlers ------------------------------------------------------
  const DRAG_THRESHOLD = 6;

  const onTilePointerDown = useCallback((e, tile) => {
    if (over) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDrag({
      tileId: tile.id,
      value: tile.value,
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      tileW: rect.width,
      tileH: rect.height,
      pointerId: e.pointerId,
      isDragging: false,
    });
  }, [over]);

  // Window-level move/up listeners while dragging
  useEffect(() => {
    if (!drag) return;
    function onMove(ev) {
      setDrag((d) => {
        if (!d) return null;
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const isDragging = d.isDragging || Math.hypot(dx, dy) > DRAG_THRESHOLD;
        return { ...d, x: ev.clientX, y: ev.clientY, isDragging };
      });
    }
    function onUp(ev) {
      const d = drag;
      setDrag(null);
      if (!d || !d.isDragging) return;
      // Hit-test for destination cell
      const board = boardRef.current;
      if (!board) return;
      const bRect = board.getBoundingClientRect();
      const padding = 12; // matches CSS .zm-board padding
      const innerW = bRect.width - padding * 2;
      const innerH = bRect.height - padding * 2;
      const x = ev.clientX - bRect.left - padding;
      const y = ev.clientY - bRect.top - padding;
      if (x < 0 || x > innerW || y < 0 || y > innerH) return; // dropped outside
      const cellW = innerW / SIZE;
      const cellH = innerH / SIZE;
      const destCol = Math.min(SIZE - 1, Math.max(0, Math.floor(x / cellW)));
      const destRow = Math.min(SIZE - 1, Math.max(0, Math.floor(y / cellH)));

      const res = tryDragMove(state, d.tileId, destRow, destCol);
      if (res.result === "merge") {
        setState(res.state);
        if (res.state.won && !state.won) {
          setBigWin(true);
          toast.success(`Zombie King reached! +${res.mergedValue}`, { duration: 3500 });
          submitRun(res.state.score, res.state.moves);
        }
      } else if (res.result === "move") {
        setState(res.state);
      }
      // 'invalid' = snap back, do nothing
    }
    function onCancel() {
      setDrag(null);
    }
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [drag, state]);

  // Pre-compute valid drop targets (cells) for highlighting during drag
  const validCells = (() => {
    if (!drag || !drag.isDragging) return null;
    const set = new Set();
    const grid = Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
    state.tiles.forEach((t) => { grid[t.row][t.col] = t; });
    for (let r = 0; r < SIZE; r++) {
      for (let c = 0; c < SIZE; c++) {
        const cell = grid[r][c];
        if (!cell || cell.id === drag.tileId) {
          set.add(r * SIZE + c); // empty (or self-cell) — relocate target
        } else if (cell.value === drag.value) {
          set.add(r * SIZE + c); // matching tile — merge target
        }
      }
    }
    return set;
  })();

  // ---- Render -------------------------------------------------------------
  const cellPct = 100 / SIZE;

  return (
    <div className="arcade-page merge-page" data-testid="merge-board">
      <Toaster position="top-center" theme="dark" />
      <ArcadeNav />

      <div className="arcade-bg" aria-hidden="true">
        <div className="arcade-bg-glow arcade-bg-glow-1" />
        <div className="arcade-bg-glow arcade-bg-glow-2" />
        <div className="arcade-bg-grid" />
      </div>

      <main className="arcade-main">
        <div className="zm-header">
          <Link to="/" className="zc-btn ghost zm-back" data-testid="merge-back">
            <ArrowLeft size={14} /> Arcade
          </Link>
          <div className="zm-scores">
            <div className="zm-score" data-testid="merge-score">
              <span className="zm-score-label">Score</span>
              <b>{state.score.toLocaleString()}</b>
            </div>
            <div className="zm-score" data-testid="merge-best">
              <span className="zm-score-label">Best</span>
              <b>{bestScore.toLocaleString()}</b>
            </div>
          </div>
          <button
            type="button"
            className="zc-btn ghost"
            onClick={handleNew}
            data-testid="merge-new"
          >
            <RotateCcw size={14} /> New
          </button>
        </div>

        <p className="zm-instructions">
          Drag a tile onto a matching tile to merge them. Empty cells move freely.
          Reach the <b>Zombie King ({WIN_VALUE})</b> to win.
        </p>

        <div className="zm-board-wrap">
          <div className="zm-board" ref={boardRef} data-testid="merge-grid">
            {/* Background cells (with optional drop highlight) */}
            <div className="zm-cells">
              {Array.from({ length: SIZE * SIZE }).map((_, i) => (
                <div
                  key={i}
                  className={`zm-cell ${validCells && validCells.has(i) ? "drop-ok" : ""}`}
                />
              ))}
            </div>

            {/* Tiles layer */}
            <div className="zm-tiles">
              {state.tiles.map((t) => {
                const meta = tileMeta(t.value);
                const img = tileImage(t.value);
                const isDraggingThis = drag && drag.tileId === t.id && drag.isDragging;
                return (
                  <div
                    key={t.id}
                    className={`zm-tile ${t.isNew ? "is-new" : ""} ${t.justMerged ? "is-merged" : ""} ${isDraggingThis ? "is-dragging-source" : ""}`}
                    style={{
                      left: `${t.col * cellPct}%`,
                      top: `${t.row * cellPct}%`,
                      width: `${cellPct}%`,
                      height: `${cellPct}%`,
                      ["--tile-bg"]: meta.bg,
                      ["--tile-fg"]: meta.fg,
                      ["--tile-glow"]: meta.glow,
                    }}
                    onPointerDown={(e) => onTilePointerDown(e, t)}
                    data-testid={`merge-tile-${t.value}`}
                  >
                    <div className="zm-tile-inner">
                      {img ? (
                        <img src={img} alt={meta.name} className="zm-tile-img" draggable={false} />
                      ) : null}
                      <span className="zm-tile-value">{t.value}</span>
                      <span className="zm-tile-name">{meta.name}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {(over || bigWin) && (
              <div
                className="zm-overlay"
                data-testid={over ? "merge-game-over" : "merge-big-win"}
              >
                <div className="zm-overlay-card">
                  {bigWin && !over ? (
                    <>
                      <Trophy size={32} />
                      <h2>You crowned the Zombie King!</h2>
                      <p>Final score: <b>{state.score.toLocaleString()}</b></p>
                      <div className="zm-overlay-buttons">
                        <button
                          type="button"
                          className="zc-btn primary"
                          onClick={() => setBigWin(false)}
                          data-testid="merge-keep-going"
                        >
                          Keep going
                        </button>
                        <button
                          type="button"
                          className="zc-btn ghost"
                          onClick={handleNew}
                          data-testid="merge-new-after-win"
                        >
                          <RotateCcw size={14} /> New game
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2>Board full</h2>
                      <p>Final score: <b>{state.score.toLocaleString()}</b></p>
                      <p className="zm-overlay-best">Best tile: <b>{state.best}</b></p>
                      <button
                        type="button"
                        className="zc-btn primary"
                        onClick={handleNew}
                        data-testid="merge-new-after-loss"
                      >
                        <RotateCcw size={14} /> Play again
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Drag ghost — renders at pointer position via portal */}
        {drag && drag.isDragging && createPortal(
          <div
            className="zm-drag-ghost"
            style={{
              left: drag.x - drag.offsetX,
              top: drag.y - drag.offsetY,
              width: drag.tileW,
              height: drag.tileH,
              ["--tile-bg"]: tileMeta(drag.value).bg,
              ["--tile-fg"]: tileMeta(drag.value).fg,
              ["--tile-glow"]: tileMeta(drag.value).glow,
            }}
            aria-hidden="true"
          >
            <div className="zm-tile-inner">
              {tileImage(drag.value) && (
                <img src={tileImage(drag.value)} alt="" className="zm-tile-img" draggable={false} />
              )}
              <span className="zm-tile-value">{drag.value}</span>
              <span className="zm-tile-name">{tileMeta(drag.value).name}</span>
            </div>
          </div>,
          document.body
        )}

        {!user && (
          <div className="zm-anon-note">
            <Coins size={12} /> Sign in to earn coins for your merges.
          </div>
        )}

        <GameSEOContent gameId="zombie-merge" />
        <ArcadeFooter />
      </main>
    </div>
  );
}
